import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import {
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ComponentRef,
  NgZone,
  ViewContainerRef
} from '@angular/core';
import { ListTasksGroupComponent } from '@shared/modules/list/components/list-tasks/components/list-tasks-group/list-tasks-group.component';
import {
  ListTaskEvent,
  ListTaskEventType,
  ListTaskService
} from '@shared/modules/list/services/list-task.service';
import { Task } from '@shared/models/task.model';
import { Subscription, throwError } from 'rxjs';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { ListTasksSingleTaskComponent } from '@shared/modules/list/components/list-tasks/components/list-tasks-single-task/list-tasks-single-task.component';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { StorageService } from '@core/services/storage.service';
import { ListService } from '@shared/modules/list/services/list.service';
import * as moment from 'moment';
import { Config } from '@shared/configs/config';
import { MyWorkService } from '@modules/my-work/shared/services/my-work.service';
import { TaskType } from '@shared/enums/task-type.enum';
import { ActivatedRoute } from '@angular/router';
import { AppInjector } from '@shared/services/app-injector.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpError } from '@shared/interfaces/error.interface';
import { arrMove } from '@shared/helpers/arr-move.helper';
import { PermissionsGroups } from '@core/permissions/permissions.group';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { catchError, tap } from 'rxjs/operators';
import { TaskGroup } from '@shared/models/task-group.model';

export class ListTasksController {
  container: ViewContainerRef;
  projectTasksContainer: ViewContainerRef;
  withoutProjectTasksContainer: ViewContainerRef;
  listGroupsComponents: { [key: number]: ComponentRef<ListTasksGroupComponent> } = {};
  tasksComponents: { [key: number]: ComponentRef<ListTasksSingleTaskComponent> } = {};
  projectApiTaskService: ProjectTaskAPIService;
  myWorkService: MyWorkService;
  sub: Subscription = new Subscription();
  private readonly injector = AppInjector.getInjector();
  route: ActivatedRoute;
  sidenavService: SidenavService;

  constructor(
    public resolver: ComponentFactoryResolver,
    public changes: ChangeDetectorRef,
    public zone: NgZone,
    public listTaskService: ListTaskService,
    public store: StorageService,
    public listService: ListService,
    private s: SnackBarService,
    private t: TranslateService
  ) {
    this.route = this.injector.get(ActivatedRoute);
    this.sidenavService = this.injector.get(SidenavService);
    this.listenListServiceEvents();
  }

  private listenListServiceEvents() {
    this.sub = this.listTaskService.emitter.subscribe((event: ListTaskEvent) => {
      switch (event.type) {
        case ListTaskEventType.ADD_TASK:
          this.addNewTaskToGroup(event.data);
          break;
        case ListTaskEventType.ADD_SUB_TASK:
          this.addNewSubtask(event.data);
          break;
        case ListTaskEventType.ADD_TASK_GROUP:
          this.addNewTaskGroup(event.data.taskGroup, event.data.groupType);
          break;
        case ListTaskEventType.UPDATE_TASK:
          this.updateTask(event.data?.task, event.data?.ignoreTasksRefresh);
          break;
        case ListTaskEventType.REMOVE_TASK:
          const { task, closeSidenav } = event.data;
          this.removeTask(task, closeSidenav);
          break;
        case ListTaskEventType.UPDATE_COUNTERS:
          this.updateCounters(event.data?.projectId, event.data?.groupType);
          break;
        default:
          break;
      }
    });
  }

  handleReorder(taskGroup, _index: number, ignoreDirection: boolean = false) {
    let listGroupTasks = [...this.listTaskService.listGroupTasksHashMap.values()];

    if (taskGroup.sortOrder < _index && !ignoreDirection) {
      _index--;
    }

    if (this.listTaskService.isMyWorkTaskLists) {
      let withProjectListGroupTasks = listGroupTasks.filter((g) => g.isMyWorkGroup && g.withProject);
      let withoutProjectListGroupTasks = listGroupTasks.filter((g) => g.isMyWorkGroup && !g.withProject);

      const isWithProject = taskGroup.type === 'project';
      const groupToModify = isWithProject ? withProjectListGroupTasks : withoutProjectListGroupTasks;

      const oldIndex = groupToModify.findIndex((g) => g.taskGroup.id === taskGroup.id);
      const newGroupList = arrMove(groupToModify, oldIndex, _index).map((group, i) => {
        group.taskGroup.sortOrder = i + 1;
        return group;
      });

      if (isWithProject) {
        withProjectListGroupTasks = newGroupList;
      } else {
        withoutProjectListGroupTasks = newGroupList;
      }

      listGroupTasks = [...withProjectListGroupTasks, ...withoutProjectListGroupTasks];

      listGroupTasks.forEach((group) => {
        this.listTaskService.listGroupTasksHashMap.set(
          `${ListTaskService.LIST_GROUP_TASK_PREFIX}${group.taskGroup.id}`,
          group
        );
      });
    } else {
      const oldIndex = listGroupTasks.findIndex((g) => g.taskGroup.id === taskGroup.id);
      listGroupTasks = arrMove(listGroupTasks, oldIndex, _index).map((group, i) => {
        group.taskGroup.sortOrder = i + 1;
        return group;
      });

      listGroupTasks.forEach((group) => {
        this.listTaskService.listGroupTasksHashMap.set(
          `${ListTaskService.LIST_GROUP_TASK_PREFIX}${group.taskGroup.id}`,
          group
        );
      });
    }

    this.changes.detectChanges();
    this.dragReorderGroups(listGroupTasks, taskGroup, _index);
    this.changes.detectChanges();

    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
    this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_COUNTERS });
  }

  onDataLoad() {
    this.createListGroupComponents();
    this.createListSingleTaskComponents();
  }

  createListGroupComponents() {
    for (const [_, listGroupTasks] of this.listTaskService.listGroupTasksHashMap) {
      listGroupTasks.tasks = listGroupTasks.tasks.sort((a, b) => a.sortOrder - b.sortOrder);
      this.listTaskService.sortTaskInHashmap();
      if (
        Object.values(this.listGroupsComponents).length &&
        this.listGroupsComponents[listGroupTasks.taskGroup.id]
      ) {
        this.setListGroupOrder(listGroupTasks);
      } else {
        this.createListGroupComponent(listGroupTasks);
      }
    }
    this.checkDeletedGroups();
  }

  private createListSingleTaskComponents() {
    this.createListSingleTaskComponent(0);
    this.checkDeletedTasks();
  }

  private createListSingleTaskComponent(index = 0, iterations = 1): void {
    const createFunc = (idx: number, it: number) => {
      it++;

      let allTasks = Array.from(
        new Map([
          ...this.listTaskService.tasksHashMap,
          ...this.listTaskService.completedTasksHashMap,
          ...this.listTaskService.cancelledTasksHashMap
        ]).values()
      );

      allTasks = this.listTaskService.isMyWorkTaskLists
        ? allTasks.sort((a, b) => (a.workspaceTaskGroupOrder ?? 0) - (b.workspaceTaskGroupOrder ?? 0))
        : allTasks.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      const task = allTasks[idx];
      if (!task) return;

      const shouldSkip =
        task.deleted ||
        task.type === TaskType.TYPE_CHANGE ||
        this.notDisplaySubtaskOnList(task) ||
        (!this.listTaskService.isMyWorkTaskLists &&
          (!task.project || task.project.id !== this.listTaskService.projectId));

      if (shouldSkip) {
        createFunc(idx + 1, it);
        return;
      }

      if (this.tasksComponents[task.id]) {
        this.setListSingleTaskContainer(task);
        createFunc(idx + 1, it);
        return;
      }

      const groupId = this.listTaskService.isMyWorkTaskLists
        ? task.workspaceTaskGroup?.id
        : task.taskGroup?.id;

      const listGroupComponent = this.listGroupsComponents[groupId];

      if (!listGroupComponent) {
        createFunc(idx + 1, it);
        return;
      }

      this.zone.runOutsideAngular(() => {
        this.prepareListSingleComponent(task, listGroupComponent, idx, !!allTasks[idx + 1]);
        listGroupComponent.instance.changes.detectChanges();
      });
    };

    if (iterations > 10) {
      setTimeout(() => createFunc(index, 0), 60);
    } else {
      createFunc(index, iterations);
    }
  }

  getParent(task: Task): Task {
    const parentTask = task.parent || this.listTaskService.getTask(task.parentId);
    if (!parentTask) {
      return task;
    }
    return parentTask;
  }

  prepareListSingleComponent(
    task: Task,
    listGroupComponent: ComponentRef<ListTasksGroupComponent>,
    index: number,
    isNextKey: boolean
  ) {
    const container =
      task.status === TaskStatus.COMPLETED
        ? listGroupComponent.instance.completedContainer
        : task.status === TaskStatus.CANCELLED
          ? listGroupComponent.instance.cancelledContainer
          : listGroupComponent.instance.tasksContainer;
    const factory = this.resolver.resolveComponentFactory(ListTasksSingleTaskComponent);
    const safeIndex = Math.min(index, container.length);
    const component = container.createComponent<ListTasksSingleTaskComponent>(factory, safeIndex);
    this.tasksComponents[task.id] = component;

    task.parentId || task.parent ? (component.instance.parentId = task.parentId || task.parent.id) : '';
    component.instance.manuallySetTask(task);
    component.instance.dataFromHashmap = true;

    const sub = component.instance.initEmitter.subscribe(() => {
      component.instance.renderer.setStyle(
        component.instance.elRef.nativeElement,
        'order',
        this.listTaskService.isMyWorkTaskLists ? task.workspaceTaskGroupOrder : task.taskGroupOrder
      );
      isNextKey ? this.createListSingleTaskComponent(index + 1) : '';
      sub.unsubscribe();
    });
    component.instance?.changes?.detectChanges();
  }

  private getTargetContainer(task: Task): 'tasks' | 'completed' | 'cancelled' {
    switch (task.status) {
      case TaskStatus.COMPLETED:
        return 'completed';
      case TaskStatus.CANCELLED:
        return 'cancelled';
      default:
        return 'tasks';
    }
  }

  private setListSingleTaskContainer(task: Task): void {
    this.zone.runOutsideAngular(() => {
      const taskCmp = this.tasksComponents[task?.id];
      if (!taskCmp) return;

      const groupId = this.listTaskService.isMyWorkTaskLists
        ? task.workspaceTaskGroup?.id
        : task.taskGroup?.id;

      const target = this.getTargetContainer(task);

      for (const key in this.listGroupsComponents) {
        const groupCmp = this.listGroupsComponents[key];

        const indexInTasks = groupCmp.instance.tasksContainer.indexOf(taskCmp.hostView);
        const indexInCompleted = groupCmp.instance.completedContainer.indexOf(taskCmp.hostView);
        const indexInCancelled = groupCmp.instance.cancelledContainer.indexOf(taskCmp.hostView);

        if (indexInTasks !== -1) {
          groupCmp.instance.tasksContainer.detach(indexInTasks);
        }
        if (indexInCompleted !== -1) {
          groupCmp.instance.completedContainer.detach(indexInCompleted);
        }
        if (indexInCancelled !== -1) {
          groupCmp.instance.cancelledContainer.detach(indexInCancelled);
        }

        if (groupCmp.instance.taskGroupId === groupId && !taskCmp.hostView.destroyed) {
          switch (target) {
            case 'tasks':
              groupCmp.instance.tasksContainer.insert(taskCmp.hostView);
              break;
            case 'completed':
              groupCmp.instance.completedContainer.insert(taskCmp.hostView);
              break;
            case 'cancelled':
              groupCmp.instance.cancelledContainer.insert(taskCmp.hostView);
              break;
          }
        }
      }

      taskCmp.instance.setTask();
      taskCmp.instance.renderer.setStyle(
        taskCmp.instance.elRef.nativeElement,
        'order',
        this.listTaskService.isMyWorkTaskLists
          ? taskCmp.instance.task.workspaceTaskGroupOrder
          : taskCmp.instance.task.taskGroupOrder
      );
    });
  }

  private setSorts() {
    Object.values(this.tasksComponents).map((taskComponent: ComponentRef<ListTasksSingleTaskComponent>) => {
      taskComponent.instance.renderer.setStyle(
        taskComponent.instance.elRef.nativeElement,
        'order',
        this.listTaskService.isMyWorkTaskLists
          ? taskComponent.instance.task.workspaceTaskGroupOrder
          : taskComponent.instance.task.taskGroupOrder
      );
    });
  }

  private setListGroupOrder(listGroupTask: ListGroupTask) {
    const listGroupComponent = this.listGroupsComponents[listGroupTask.taskGroup.id];
    listGroupComponent.instance.renderer.setStyle(
      listGroupComponent.instance.elRef.nativeElement,
      'order',
      listGroupTask.taskGroup.sortOrder
    );
  }

  private createListGroupComponent(listGroupTask: ListGroupTask) {
    let component: ComponentRef<ListTasksGroupComponent>;
    this.zone.runOutsideAngular(() => {
      if (listGroupTask.isMyWorkGroup) {
        const isGroupWithProject = listGroupTask.withProject;
        component = this.createComponent(isGroupWithProject ? 'project' : 'without_project');
      } else {
        component = this.createComponent();
      }

      component.instance.taskGroupId = listGroupTask.taskGroup.id;

      if (this.listTaskService.project) {
        const project = this.listTaskService.project;
        component.instance.dragPermissions = {
          group: PermissionsGroups.PROJECTS,
          action: 'DRAG_PROJECT_TASKS',
          objectCreatorId: [
            project?.basicDataBox?.responsibleContact?.contact?.employee?.id,
            project?.basicDataBox?.responsibleEmployee?.id,
            project?.MainContact?.employee?.id
          ].filter(Boolean)
        };
      }

      const sub = component.instance.initEmitter.subscribe(() => {
        component.instance.renderer.setStyle(
          component.instance.elRef.nativeElement,
          'order',
          listGroupTask.taskGroup.sortOrder
        );
        sub.unsubscribe();
      });
      this.listGroupsComponents[listGroupTask.taskGroup.id] = component;
      this.changes.detectChanges();
    });
  }

  destroyTasks() {
    Object.keys(this.tasksComponents).forEach((taskId: string) => {
      this.tasksComponents[taskId].destroy();
    });
    Object.keys(this.listGroupsComponents).forEach((taskGroupId: string) => {
      this.listGroupsComponents[taskGroupId].destroy();
    });

    this.listGroupsComponents = {};
    this.tasksComponents = {};
  }

  private checkDeletedGroups() {
    Object.keys(this.listGroupsComponents).map((taskGroupId: string) => {
      if (
        !this.listTaskService.listGroupTasksHashMap.get(
          `${ListTaskService.LIST_GROUP_TASK_PREFIX}${taskGroupId}`
        )
      ) {
        this.listGroupsComponents[taskGroupId].destroy();
        delete this.listGroupsComponents[taskGroupId];
      }
    });
  }

  dragReorderGroups(listGroupTasks, taskGroup, targetIndex) {
    this.listTaskService.clearGroups();
    this.listTaskService.listGroupsTask = listGroupTasks;
    this.onDataLoad();
    this.reorderGroups();
    this.changes.detectChanges();

    if (this.listTaskService.isMyWorkTaskLists) {
      this.projectApiTaskService
        .workspaceGroupOrderChange(taskGroup.id, targetIndex + 1)
        .pipe(
          tap(() => this.s.success(this.t.instant('ProjectTasks.TaskGroup.groupMoveSuccess'))),
          catchError((error: HttpError) => {
            this.s.error(this.t.instant('ProjectTasks.TaskGroup.groupMoveError'));
            return throwError(error);
          })
        )
        .subscribe();
    } else {
      this.sendBulkEdit();
    }
  }

  prepareGroupOrders() {
    let listGroupTasks = [...this.listTaskService.listGroupTasksHashMap.values()];

    let groupsOrders = listGroupTasks.map((listGroupTasks: ListGroupTask, index) => {
      let obj = {
        id: listGroupTasks.taskGroup?.id,
        order: listGroupTasks.taskGroup.sortOrder
      };
      !listGroupTasks.taskGroup.id ? (obj['name'] = listGroupTasks.taskGroup.name) : '';
      return obj;
    });

    return groupsOrders;
  }

  sendBulkEdit() {
    let groupsOrders = this.prepareGroupOrders();
    this.projectApiTaskService
      .taskGroupBulkEdit({
        groupsOrders: groupsOrders,
        projectId: this.listTaskService.projectId,
        type: 'task'
      })
      .subscribe({
        next: () => {},
        error: (error: HttpError) => {
          if (error.messageCode === 'intilio_16') {
            this.s.error(
              this.t.instant('ProjectTasks.RightSection.errorExistGroup', { groupName: error?.data?.name })
            );
          } else {
            this.s.error(this.t.instant('ProjectTasks.RightSection.errorSaveGroups'));
          }
        }
      })
      .add(() => {
        this.changes.detectChanges();
      });
  }

  reorderGroups(): void {
    const groupsArray = [...this.listTaskService.listGroupTasksHashMap.values()];
    const withProjectGroups = groupsArray.filter((r) => r.isMyWorkGroup && r.withProject);
    const withoutProjectGroups = groupsArray.filter((r) => r.isMyWorkGroup && !r.withProject);

    withProjectGroups.forEach((group, index) => (group.taskGroup.sortOrder = index));
    withoutProjectGroups.forEach((group, index) => (group.taskGroup.sortOrder = index));

    Object.keys(this.listGroupsComponents).forEach((id: string) => {
      const group = this.listTaskService.listGroupTasksHashMap.get(
        `${ListTaskService.LIST_GROUP_TASK_PREFIX}${id}`
      );
      const view = this.listGroupsComponents[id].hostView;

      if (group.isMyWorkGroup) {
        const container = group.withProject ? this.projectTasksContainer : this.withoutProjectTasksContainer;
        container.move(view, group.taskGroup.sortOrder);
      } else {
        this.container.move(view, group.taskGroup.sortOrder - 1);
      }
    });
  }

  notDisplaySubtaskOnList(task: Task) {
    if (!task) {
      return false;
    }

    const parent = this.getParent(task);
    if (this.listTaskService.isMyWorkTaskLists) {
      return (
        parent?.id !== task?.id &&
        isNotNullOrUndefined(task.parentId || task.parent) &&
        parent.status !== TaskStatus.COMPLETED
      ); //subtask can be in not completed list if the highest parent is completed
    } else {
      return (
        parent?.id !== task?.id &&
        isNotNullOrUndefined(task.parentId || task.parent) &&
        this.listTaskService.getTask(task.parentId)?.taskGroup?.id === task.taskGroup?.id && //subtask can be in few places if has different taskGroup than parent
        parent.status !== TaskStatus.COMPLETED
      ); //subtask can be in not completed list if the highest parent is completed
    }
  }

  private checkDeletedTasks() {
    Object.values(this.tasksComponents).map((taskComponent: ComponentRef<ListTasksSingleTaskComponent>) => {
      const task = this.listTaskService.getTask(taskComponent.instance.taskId);

      if (!task) {
        taskComponent.destroy();
        delete this.tasksComponents[taskComponent.instance.taskId];
        return;
      }

      if (task.type === TaskType.TYPE_CHANGE) {
        taskComponent.destroy();
        delete this.tasksComponents[taskComponent.instance.taskId];
        return;
      }

      if (
        !this.listTaskService.isMyWorkTaskLists &&
        (!task.project || task.project.id !== this.listTaskService.projectId)
      ) {
        taskComponent.destroy();
        delete this.tasksComponents[taskComponent.instance.taskId];
        return;
      }

      if (this.listTaskService.isMyWorkTaskLists && !this.isTaskToAddInMyWorkView(task)) {
        taskComponent.destroy();
        delete this.tasksComponents[taskComponent.instance.taskId];
        return;
      }

      if (this.notDisplaySubtaskOnList(task)) {
        taskComponent.destroy();
        delete this.tasksComponents[task.id];
        return;
      }
    });
  }

  private updateTask(task: Task, ignoreTasksRefresh = false): void {
    if (this.listTaskService.isMyWorkTaskLists && !this.isTaskToAddInMyWorkView(task)) {
      return this.removeTask(task);
    }

    // if (task.type === TaskType.TYPE_CHANGE) return this.removeTask(task);
    // const existingTask = this.listTaskService.tasksHashMap.get(this.listTaskService.getKeyForTask(task));
    // if (existingTask?.type && task.isChange) return this.removeTask(task);

    const mapForStatus = (status: TaskStatus) => {
      switch (status) {
        case TaskStatus.COMPLETED:
          return this.listTaskService.completedTasksHashMap;
        case TaskStatus.CANCELLED:
          return this.listTaskService.cancelledTasksHashMap;
        default:
          return this.listTaskService.tasksHashMap;
      }
    };

    const key = this.listTaskService.getKeyForTask(task);
    const destinationMap = mapForStatus(task.status);

    [
      this.listTaskService.tasksHashMap,
      this.listTaskService.completedTasksHashMap,
      this.listTaskService.cancelledTasksHashMap
    ].forEach((m) => m?.delete(key));

    destinationMap.set(key, new Task(task));

    this.updateSubtasks(task);

    this.listTaskService.sortTaskInHashmap();

    if (!ignoreTasksRefresh) this.createListSingleTaskComponents();

    this.setSorts();

    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
  }

  // private updateSubtasks(newTask: Task) {
  //   const setGroupToChild = (task: Task) => {
  //     task.children.map((subtask: Task) => {
  //       if (!subtask.deleted) {
  //         if (this.isTaskCompletedOrCanceled(subtask)) {
  //           this.listTaskService.completedTasksHashMap.set(
  //             this.listTaskService.getKeyForTask(subtask),
  //             new Task(subtask)
  //           );
  //         } else {
  //           this.listTaskService.tasksHashMap.set(
  //             this.listTaskService.getKeyForTask(subtask),
  //             new Task(subtask)
  //           );
  //         }
  //       }
  //       setGroupToChild(subtask);
  //     });
  //   };
  //   setGroupToChild(newTask);
  // }
  private updateSubtasks(parent: Task): void {
    const pickMap = (t: Task): Map<string, Task> => {
      switch (t.status) {
        case TaskStatus.COMPLETED:
          return this.listTaskService.completedTasksHashMap;
        case TaskStatus.CANCELLED:
          return this.listTaskService.cancelledTasksHashMap;
        default:
          return this.listTaskService.tasksHashMap;
      }
    };

    const iterate = (task: Task) => {
      task.children
        ?.filter((st) => !st.deleted)
        .forEach((sub) => {
          const key = this.listTaskService.getKeyForTask(sub);

          this.listTaskService.tasksHashMap.delete(key);
          this.listTaskService.completedTasksHashMap.delete(key);
          this.listTaskService.cancelledTasksHashMap?.delete?.(key);

          pickMap(sub).set(key, new Task(sub));

          iterate(sub);
        });
    };

    iterate(parent);
  }

  private removeTask(task: Task, closeSidenav?: boolean) {
    this.updateParentRemovedSubtask(task);
    this.listTaskService.removeTask(task);
    if (this.tasksComponents[task.id]) {
      this.tasksComponents[task.id].destroy();
      delete this.tasksComponents[task.id];
    }
    this.listTaskService.sortTaskInHashmap();
    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
    this.listTaskService.emitter.emit({
      type: ListTaskEventType.UPDATE_COUNTERS,
      data: { projectId: task?.project?.id, groupType: task?.type }
    });
    if (closeSidenav) this.sidenavService.close();
  }

  updateParentRemovedSubtask(subtask: Task) {
    if (subtask?.parentId) {
      let task = this.listTaskService.getTask(subtask.parentId);
      task.children = task.children.map((subt: Task) => {
        if (subt.id === subtask.id) {
          subt.deleted = moment().format(Config.DATE_SERVER);
        }
        return subt;
      });

      task.children?.filter((i) => !i.deleted).length === 0 ? (task.openedSubtasks = false) : null;
      this.listTaskService.updateTask(task);
    }
  }

  private addNewTaskGroup(taskGroup: TaskGroup, groupType: string) {
    let listGroupTasks = [...this.listTaskService.listGroupTasksHashMap.values()];

    const isMyWork = this.listTaskService.isMyWorkTaskLists;
    const isWithProject = groupType === 'project';

    if (isMyWork) {
      const relevantGroup = listGroupTasks.filter((g) => g.isMyWorkGroup && g.withProject === isWithProject);
      taskGroup.sortOrder = relevantGroup.length + 1;
    } else {
      taskGroup.sortOrder = listGroupTasks.length + 1;
    }

    const listGroupTask: ListGroupTask = {
      allCount: 0,
      completedCount: 0,
      cancelledCount: 0,
      currentCompletedCount: 0,
      currentAllCount: 0,
      tasks: [],
      completedTasks: [],
      cancelledTasks: [],
      taskGroup: taskGroup,
      projectGroup: true,
      isMyWorkGroup: isMyWork,
      withProject: isWithProject
    };

    this.listTaskService.listGroupTasksHashMap.set(
      `${ListTaskService.LIST_GROUP_TASK_PREFIX}${taskGroup.id}`,
      listGroupTask
    );
    this.createListGroupComponent(listGroupTask);
    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
    return listGroupTask;
  }

  private addNewSubtask(subtask: Task) {
    const taskFromMap = this.listTaskService.getTask(subtask.parent?.id);
    if (taskFromMap) {
      taskFromMap.children.unshift(subtask);
      this.listTaskService.updateTask(taskFromMap);
    }
    this.listTaskService.addTask(subtask);
    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
  }

  private addNewTaskToGroup(task: Task) {
    if (this.listTaskService.isMyWorkTaskLists && !this.isTaskToAddInMyWorkView(task, true)) {
      return;
    }

    if (task.type === TaskType.TYPE_CHANGE) return;

    this.listTaskService.addTask(task);
    this.listTaskService.sortTaskInHashmap();
    this.createListSingleTaskComponents();
    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
    this.listTaskService.emitter.emit({
      type: ListTaskEventType.UPDATE_COUNTERS,
      data: { projectId: task?.project?.id, groupType: task?.type }
    });
  }

  private isTaskToAddInMyWorkView(task: Task, creating?: boolean) {
    if (!task) return false;
    const amIAssigned = [
      ...task.assignedTo.filter((e) => e.id === this.store.Employee.id),
      ...(task.parentId && task.parent
        ? task.parent?.assignedTo?.filter((e) => e.id === this.store.Employee.id)
        : [])
    ];

    const url = new URL(window.location.href);

    if (Number(url.searchParams.get('delegatedTasks')) === 1) {
      if (!task.assignedEmails?.length && !task.assignedTo?.length && !task.assignedUsers?.length) {
        return false;
      }

      if (creating) {
        if (task.assignedTo?.length || task.assignedEmails?.length) {
          return true;
        }
      } else {
        return task?.isDelegated;
      }
    } else {
      if (amIAssigned?.length) {
        return true;
      }
      if (
        task.creator?.id === this.store.Employee?.id &&
        Number(url.searchParams.get('filters[showUnassigned]')) !== undefined
      ) {
        return true;
      }
    }

    return false;
  }

  private createComponent(groupType?: string): ComponentRef<ListTasksGroupComponent> {
    const factory = this.resolver.resolveComponentFactory(ListTasksGroupComponent);

    if (groupType === 'project') {
      return this.projectTasksContainer.createComponent<ListTasksGroupComponent>(factory);
    }

    if (groupType === 'without_project') {
      return this.withoutProjectTasksContainer.createComponent<ListTasksGroupComponent>(factory);
    }

    return this.container.createComponent<ListTasksGroupComponent>(factory);
  }

  private updateCounters(projectId?: number, groupType?: string) {
    const service = this.listTaskService.isMyWorkTaskLists ? this.myWorkService : this.projectApiTaskService;
    const queryParams = Object.assign({}, this.route.snapshot.queryParams);
    const paramsToDelete = ['modalData', 'backdrop', 'modal', 'config'];
    paramsToDelete?.forEach((key: string) => delete queryParams[key]);

    if (!this.listTaskService.isMyWorkTaskLists && !projectId && !this.listTaskService.projectId) return;

    service
      .getListGroupTasksCounters(projectId ?? this.listTaskService.projectId, {
        ...queryParams,
        type: groupType ?? TaskType.TYPE_TASK
      })
      .subscribe({
        next: (listGroupTasks: ListGroupTask[]) => {
          const updateHash = (id: number, stats: any) => {
            const key = `${ListTaskService.LIST_GROUP_TASK_PREFIX}${id}`;
            const group = this.listTaskService.listGroupTasksHashMap.get(key);
            if (group) {
              this.copyStats(group, stats);
              this.listTaskService.listGroupTasksHashMap.set(key, group);
            }
          };

          if (this.listTaskService.isMyWorkTaskLists) {
            listGroupTasks.forEach((container) =>
              (container.data ?? []).forEach((nested) => {
                updateHash(nested.workspaceTaskGroup?.id, {
                  ...nested,
                  allCompletedCount: container.allCompletedCount,
                  allCancelledCount: container.allCancelledCount,
                  allCount: container.allCount
                });

                (nested.tasks ?? []).forEach((nestedTask) => {
                  const key = `${ListTaskService.TASK_PREFIX}${nestedTask.id}`;
                  const task = this.listTaskService.tasksHashMap.get(key);
                  if (task) {
                    this.listTaskService.tasksHashMap.set(key, {
                      ...task,
                      completedChildrenCount: nestedTask.completedChildrenCount,
                      inProgressChildrenCount: nestedTask.inProgressChildrenCount
                    } as Task);
                  }
                });
              })
            );
          } else {
            listGroupTasks.forEach((g) => updateHash(g.taskGroup?.id, g));
          }

          this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
        }
      });
  }

  copyStats(dst: any, src: any): void {
    [
      'completedCount',
      'cancelledCount',
      'allCompletedCount',
      'allCancelledCount',
      'allCount',
      'count'
    ].forEach((key) => (dst[key] = src[key]));

    dst.currentAllCount = src.count;
  }

  handleDrop(task: Task, targetGroup: ListGroupTask, targetIndex: number): void {
    const isMyWork = this.listTaskService.isMyWorkTaskLists;
    const currentGroupId = isMyWork ? task.workspaceTaskGroup?.id : task.taskGroup?.id;

    const currentGroup = this.listTaskService.listGroupTasksHashMap.get(
      `${ListTaskService.LIST_GROUP_TASK_PREFIX}${currentGroupId}`
    );

    // Sort tasks in group based on order number
    const sortedTasks = [...currentGroup.tasks].sort((a, b) => {
      const orderA = isMyWork ? (a.workspaceTaskGroupOrder ?? 0) : (a.sortOrder ?? a.taskGroupOrder ?? 0);
      const orderB = isMyWork ? (b.workspaceTaskGroupOrder ?? 0) : (b.sortOrder ?? b.taskGroupOrder ?? 0);
      return orderA - orderB;
    });

    // Find current index in group
    const currentIndex = sortedTasks.findIndex((t) => t.id === task.id);

    const isCurrentGroup = currentGroupId === targetGroup.taskGroup.id;

    // Check if we are moving to the same place
    if (isCurrentGroup && currentIndex + 1 === targetIndex) return;

    // Adjust target index because lib set target index + 1 when moving down
    if (isCurrentGroup && targetIndex > currentIndex) targetIndex--;

    if (targetGroup.allCount === 0) targetIndex = 0;

    if (this.tasksComponents[task.id]) {
      this.tasksComponents[task.id].destroy();
      delete this.tasksComponents[task.id];
    }

    task.taskGroupOrder = targetIndex;
    task.sortOrder = targetIndex + 1;
    task.workspaceTaskGroupOrder = targetIndex + 1;

    this.listTaskService.updateTaskInHashGroup(task);

    this.listTaskService.moveTaskToGroup(task, targetGroup, targetIndex);

    this.createTaskInGroup(task, targetGroup, targetIndex);

    this.listTaskService.emitter.emit({ type: ListTaskEventType.REFRESH_LIST_GROUPS });
  }

  createTaskInGroup(task: Task, group: ListGroupTask, index: number) {
    const groupComponent = this.listGroupsComponents[group.taskGroup.id];
    if (!groupComponent) return;
    this.prepareListSingleComponent(task, groupComponent, index, false);
  }
}
